/**
 * Device Fingerprinting Utility for Guest User Tracking
 * Generates unique device fingerprints for non-logged-in users
 * Uses multiple browser characteristics to create a stable identifier
 */

import Cookies from 'js-cookie'

const DEVICE_ID_KEY = 'ordyrr_device_id'
const DEVICE_ID_COOKIE = 'ordyrr_device_id'
const COOKIE_EXPIRY_DAYS = 365

/**
 * Generate a simple hash from a string
 * @param {string} str - String to hash
 * @returns {string} - Hash value
 */
const simpleHash = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Generate canvas fingerprint
 * @returns {string} - Canvas fingerprint hash
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    // Draw text with specific styling
    ctx.textBaseline = 'top'
    ctx.font = '14px "Arial"'
    ctx.textBaseline = 'alphabetic'
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('Ordyrr Device ID', 2, 15)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('Ordyrr Device ID', 4, 17)
    
    // Get canvas data
    const dataURL = canvas.toDataURL()
    return simpleHash(dataURL)
  } catch (error) {
    console.warn('Canvas fingerprint failed:', error)
    return 'canvas_unavailable'
  }
}

/**
 * Get WebGL fingerprint
 * @returns {string} - WebGL fingerprint
 */
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    
    if (!gl) return 'webgl_unavailable'
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    if (debugInfo) {
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      return simpleHash(`${vendor}~${renderer}`)
    }
    
    return 'webgl_no_debug'
  } catch (error) {
    console.warn('WebGL fingerprint failed:', error)
    return 'webgl_unavailable'
  }
}

/**
 * Collect device information for fingerprinting
 * @returns {Object} - Device information
 */
const collectDeviceInfo = () => {
  const nav = window.navigator
  const screen = window.screen
  
  return {
    userAgent: nav.userAgent || 'unknown',
    language: nav.language || nav.userLanguage || 'unknown',
    languages: nav.languages ? nav.languages.join(',') : 'unknown',
    platform: nav.platform || 'unknown',
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    deviceMemory: nav.deviceMemory || 0,
    screenResolution: `${screen.width}x${screen.height}`,
    screenColorDepth: screen.colorDepth || 0,
    screenPixelDepth: screen.pixelDepth || 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    timezoneOffset: new Date().getTimezoneOffset(),
    touchSupport: 'ontouchstart' in window || nav.maxTouchPoints > 0,
    cookieEnabled: nav.cookieEnabled,
    doNotTrack: nav.doNotTrack || 'unknown',
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint()
  }
}

/**
 * Generate unique device fingerprint
 * @returns {Promise<string>} - Device fingerprint hash
 */
export const generateDeviceFingerprint = async () => {
  try {
    const deviceInfo = collectDeviceInfo()
    
    // Create fingerprint string from key characteristics
    const fingerprintString = [
      deviceInfo.userAgent,
      deviceInfo.language,
      deviceInfo.platform,
      deviceInfo.screenResolution,
      deviceInfo.screenColorDepth,
      deviceInfo.timezone,
      deviceInfo.timezoneOffset,
      deviceInfo.hardwareConcurrency,
      deviceInfo.canvas,
      deviceInfo.webgl
    ].join('|')
    
    // Generate hash
    const fingerprint = simpleHash(fingerprintString)
    
    console.log('🔐 Device fingerprint generated:', fingerprint)
    
    return {
      fingerprint,
      deviceInfo
    }
  } catch (error) {
    console.error('❌ Error generating device fingerprint:', error)
    // Fallback to random ID if fingerprinting fails
    const fallbackId = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    return {
      fingerprint: fallbackId,
      deviceInfo: { error: error.message }
    }
  }
}

/**
 * Store device ID in localStorage and cookie
 * @param {string} deviceId - Device fingerprint to store
 */
const storeDeviceId = (deviceId) => {
  try {
    // Store in localStorage (primary)
    localStorage.setItem(DEVICE_ID_KEY, deviceId)
    
    // Store in cookie (backup, 1 year expiry)
    Cookies.set(DEVICE_ID_COOKIE, deviceId, { expires: COOKIE_EXPIRY_DAYS })
    
    console.log('✅ Device ID stored:', deviceId)
  } catch (error) {
    console.error('❌ Error storing device ID:', error)
  }
}

/**
 * Retrieve device ID from storage
 * @returns {string|null} - Stored device ID or null
 */
export const getDeviceId = () => {
  try {
    // Try localStorage first
    let deviceId = localStorage.getItem(DEVICE_ID_KEY)
    
    // Fallback to cookie
    if (!deviceId) {
      deviceId = Cookies.get(DEVICE_ID_COOKIE)
      
      // If found in cookie, restore to localStorage
      if (deviceId) {
        localStorage.setItem(DEVICE_ID_KEY, deviceId)
        console.log('📦 Device ID restored from cookie:', deviceId)
      }
    }
    
    return deviceId
  } catch (error) {
    console.error('❌ Error retrieving device ID:', error)
    return null
  }
}

/**
 * Initialize device tracking
 * Generates or retrieves device fingerprint
 * @returns {Promise<Object>} - { deviceId, deviceInfo, isNew }
 */
export const initDeviceTracking = async () => {
  try {
    console.log('🚀 Initializing device tracking...')
    
    // Check if device ID already exists
    let existingDeviceId = getDeviceId()
    
    if (existingDeviceId) {
      console.log('✅ Existing device ID found:', existingDeviceId)
      return {
        deviceId: existingDeviceId,
        deviceInfo: null,
        isNew: false
      }
    }
    
    // Generate new device fingerprint
    const { fingerprint, deviceInfo } = await generateDeviceFingerprint()
    
    // Store the fingerprint
    storeDeviceId(fingerprint)
    
    console.log('✅ New device ID created:', fingerprint)
    
    return {
      deviceId: fingerprint,
      deviceInfo,
      isNew: true
    }
  } catch (error) {
    console.error('❌ Error initializing device tracking:', error)
    throw error
  }
}

/**
 * Validate Indian mobile phone number
 * @param {string} phone - Phone number to validate
 * @returns {Object} - { isValid, isSuspicious, reason }
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) {
    return { isValid: false, isSuspicious: false, reason: 'Phone number is required' }
  }
  
  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '')
  
  // Check if it's a valid Indian mobile number (10 digits starting with 6-9)
  const indianMobileRegex = /^[6-9]\d{9}$/
  
  if (!indianMobileRegex.test(cleanPhone)) {
    return { 
      isValid: false, 
      isSuspicious: false, 
      reason: 'Invalid Indian mobile number format. Must be 10 digits starting with 6-9' 
    }
  }
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    /^(\d)\1{9}$/, // All same digits (e.g., 1111111111)
    /^0123456789$/, // Sequential ascending
    /^9876543210$/, // Sequential descending
    /^1234567890$/, // Sequential with 0 at end
    /^0987654321$/, // Reverse sequential
  ]
  
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(cleanPhone)) {
      return {
        isValid: true,
        isSuspicious: true,
        reason: 'Phone number matches a suspicious pattern (e.g., all same digits or sequential)'
      }
    }
  }
  
  // Check for too many repeated digits (e.g., 9999988888)
  const digitCounts = {}
  for (const digit of cleanPhone) {
    digitCounts[digit] = (digitCounts[digit] || 0) + 1
  }
  
  const maxRepeats = Math.max(...Object.values(digitCounts))
  if (maxRepeats >= 7) {
    return {
      isValid: true,
      isSuspicious: true,
      reason: 'Phone number has too many repeated digits'
    }
  }
  
  return {
    isValid: true,
    isSuspicious: false,
    reason: null
  }
}

/**
 * Clear device tracking data (for testing purposes)
 */
export const clearDeviceTracking = () => {
  try {
    localStorage.removeItem(DEVICE_ID_KEY)
    Cookies.remove(DEVICE_ID_COOKIE)
    console.log('🗑️ Device tracking data cleared')
  } catch (error) {
    console.error('❌ Error clearing device tracking:', error)
  }
}

export default {
  generateDeviceFingerprint,
  initDeviceTracking,
  getDeviceId,
  validatePhoneNumber,
  clearDeviceTracking
}
